import eventMixin from "./eventMixin"
import elementMatches from "./elementMatches"

const disableSelector = "[data-billing-form-disable]"
const disableWithAttribute = "data-billing-form-disable-with"
const disableWithSelector = `[${disableWithAttribute}]`

class Form {
  static get events() {
    if (this._events === undefined) {
      this._events = {
        submit: "billing:form:submit",
        success: "billing:form:success",
        error: "billing:form:error",
        complete: "billing:form:complete",
        disable: "billing:form:disable",
        enable: "billing:form:enable"
      }
    }
    return this._events
  }

  static init(selector) {
    document.querySelectorAll(selector).forEach((element) => {
      new this(element)
    })
  }

  constructor(element) {
    this.element = element
    this.eventElement = this.element
    this.elementsEnabled = true
    this.submitEnabled = true

    if (!this.element["_billing_initialized"]) {
      this.init()
    }
  }

  // All form subclasses first need to call 'super' in init() method.
  init() {
    this.addEventListener("submit", (...args) => {
      this.stopSubmitIfDisabled(...args)
    }, { capture: true })
    this.element["_billing_initialized"] = true
  }

  get dataset() {
    return this.element.dataset
  }

  handleSubmit() {
    this.disableElements()

    this.fire("submit")
  }

  handleSuccess() {
    // Q: why arent form elements enabled on success (enableElements)?
    // A: all forms redirect to another page on form success, so there's no
    // reason to enable elements after form success and before the redirect is
    // completed.
    this.fire("success")
    this.fire("complete")
  }

  handleError() {
    this.enableElements()

    this.fire("error")
    this.fire("complete")
  }

  // Elements disable/enable methods.
  get elementsDisabled() {
    return !this.elementsEnabled
  }

  disableElements() {
    if (this.elementsEnabled) {
      this.elementsEnabled = false

      this._disableElements()
      this.fire("disable")
    }
  }

  enableElements() {
    if (this.elementsDisabled) {
      this.elementsEnabled = true

      this._enableElements()
      this.fire("enable")
    }
  }

  // Disables submit element with 'disable' attrs.
  _disableElements() {
    const disableElements = this.element.querySelectorAll(disableSelector)
    const disableWithElements =
      this.element.querySelectorAll(disableWithSelector)

    disableElements.forEach((element) => {
      element.disabled = true
    })

    // Same logic as in rails:
    // https://github.com/rails/rails/blob/de53ba56cab69fb9707785a397a59ac4aaee9d6f/actionview/app/assets/javascripts/rails-ujs/features/disable.coffee
    disableWithElements.forEach((element) => {
      if (element["billing:form:disabled"]) { return }

      const replacement = element.getAttribute(disableWithAttribute)
      if (replacement) {
        if (elementMatches(element, "button")) {
          element["billing:form:enable-with"] = element.innerHTML
          element.innerHTML = replacement
        } else {
          element["billing:form:enable-with"] = element.value
          element.value = replacement
        }
      }
      element.disabled = true
      element["billing:form:disabled"] = true
    })
  }

  _enableElements() {
    const enableElements = this.element.querySelectorAll(disableSelector)
    const enableWithElements =
      this.element.querySelectorAll(disableWithSelector)

    enableElements.forEach((element) => {
      element.disabled = false
    })

    enableWithElements.forEach((element) => {
      if (!element["billing:form:disabled"]) { return }

      const original = element["billing:form:enable-with"]
      if (original) {
        if (elementMatches(element, "button")) {
          element.innerHTML = original
        } else {
          element.value = original
        }
      }
      element.disabled = false
      // cleanup
      element["billing:form:enable-with"] = null
      element["billing:form:disabled"] = null
    })
  }

  // Submit disable/enable methods.
  get submitDisabled() {
    return !this.submitEnabled
  }

  get submitElement() {
    if (this._submitElement === undefined) {
      this._submitElement = this.element.querySelector("input[type=submit]")
    }
    return this._submitElement
  }

  disableSubmit() {
    if (this.submitEnabled) {
      this.submitEnabled = false

      // Form submitting is disabled via 'stopSubmitIfDisabled'.
      this._disableSubmitElement()
    }
  }

  enableSubmit() {
    if (this.submitDisabled) {
      this.submitEnabled = true

      this._enableSubmitElement()
    }
  }

  _disableSubmitElement() {
    this.submitElement.disabled = true
  }

  _enableSubmitElement() {
    this.submitElement.disabled = false
  }

  // private
  stopSubmitIfDisabled(event) {
    if (this.disabled || this.submitDisabled) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }
  }
}

Object.defineProperties(
  Form.prototype,
  Object.getOwnPropertyDescriptors(eventMixin)
);

export default Form
